import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { AsyncButton } from "../components/AsyncButton";
import { useIsMobile } from "../hooks/useIsMobile";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useRouteTab } from "../hooks/useRouteTab";
import { DateInput } from "../components/DateInput";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { DropdownMenu, type DropdownMenuItem } from "../components/DropdownMenu";
import {
  blockInvalidNumberKey, requiredTrimmed, requiredValue, notFutureDate, todayLocalISO, collectErrors,
} from "../utils/validate";
import {
  getExpenseCaptures, getExpenseCapture, createExpenseCapture, updateExpenseCapture, deleteExpenseCapture,
  confirmExpenseCapture, rejectExpenseCapture, reprocessExpenseCapture, resolveDuplicateExpenseCapture,
  scanExpenseCaptureReceipt, scanExpenseCaptureReceiptAsync,
  downloadExpenseCapturesExport, downloadExpenseCapturesPdf, fetchExpenseCaptureReceiptBlob,
  getBranches, getBranchesConfig, getExpenseCaptureSplits, setExpenseCaptureSplits,
  bulkActionConfirmExpenseCaptures, bulkActionRejectExpenseCaptures, bulkActionDeleteExpenseCaptures,
  getErrorMessage,
  type BulkActionResult,
} from "../api";
import ExpenseBranchesSettings from "./ExpenseBranchesSettings";
import ExpenseLedgerTab from "./ExpenseLedgerTab";
import ExpenseBulkConfirmSheet from "./ExpenseBulkConfirmSheet";
import ExpenseInsightsTab from "./ExpenseInsightsTab";
import ExpenseBranchComparison from "./ExpenseBranchComparison";

// Native theme only — no colors/fonts from the ocr-fable mockup. #1e3a8a matches this
// app's --color-primary/--color-primary-btn CSS var; the tone table below matches the
// existing STATUS_CONFIG convention already used across the app (green/amber/red/blue),
// plus one new purple tone (this app had none yet) for the WHT/recurring-vendor badges.
const NAVY = "#0f172a";
const PRIMARY = "#1e3a8a";
const POLL_INTERVAL_MS = 3000;
const MAX_BATCH_FILES = 20;
const HIGH_CONFIDENCE = 90;
const MATH_TOLERANCE = 1;

// Client-side mirror of the backend's reconcile_math/reconcile_line_items
// (expense_ocr_pipeline.py) — display-only, so a reviewer can see *why* a capture needs
// review instead of just that it does. Not the source of truth for routing (the backend
// already gates ready_to_confirm on this); this only decides whether to show a warning.
function totalsReconcile(e: any): boolean {
  const { subtotal, total } = e;
  const vatAmount = e.vat_amount ?? 0;
  if (subtotal == null || total == null) return true; // nothing to check, don't warn
  if (Math.abs((subtotal + vatAmount) - total) > MATH_TOLERANCE) return false;
  if (e.vat_rate != null) {
    const expectedVat = subtotal * (e.vat_rate / 100);
    if (Math.abs(expectedVat - vatAmount) > MATH_TOLERANCE) return false;
  }
  return true;
}

function lineItemsReconcile(e: any): boolean {
  const items = e.line_items;
  if (!Array.isArray(items) || items.length === 0) return true;
  let lineTotal = 0, sawAmount = false;
  for (const item of items) {
    if (item == null || typeof item !== "object" || item.amount == null) continue;
    sawAmount = true;
    lineTotal += item.amount;
    if (item.qty != null && item.unit_price != null) {
      if (Math.abs(item.qty * item.unit_price - item.amount) > MATH_TOLERANCE) return false;
    }
  }
  if (sawAmount && e.subtotal != null && Math.abs(lineTotal - e.subtotal) > MATH_TOLERANCE) return false;
  return true;
}

// type="number" inputs natively accept "e"/"E" (scientific notation) and "+"/"-"
// as valid keystrokes in every browser — bug-88 reported a lone "e" being
// accepted into Subtotal with no error. Block them at the key level rather
// than trying to sanitize after the fact.
function blockNonNumericKeys(e: React.KeyboardEvent<HTMLInputElement>) {
  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
}

// What to tell the user once an upload batch finishes extracting (C5-36). Exported and
// kept free of React so the wording and the tone are testable directly.
//
// The discarded case is the one that matters most: `not_an_expense` is the pipeline's
// verdict on a document it decided isn't a receipt at all (expense_ocr_pipeline.py), and
// it is a terminal status with no review queue and — until now — no filter chip. That
// combination is what made an uploaded file appear to vanish without explanation (C5-17).
export function summarizeBatchOutcome(items: any[]): { message: string; tone: "success" | "error" | "info" } {
  const n = (status: string) => items.filter(i => i.status === status).length;
  const ready = n("ready_to_confirm");
  const review = n("needs_review");
  const failed = n("extraction_failed");
  const discarded = n("discarded");

  const parts: string[] = [];
  if (ready) parts.push(`${ready} ready to confirm`);
  if (review) parts.push(`${review} needs review`);
  if (failed) parts.push(`${failed} couldn't be read`);
  if (discarded) parts.push(`${discarded} discarded — not an expense document`);

  const succeeded = ready + review;
  const problems = failed + discarded;
  const tone = problems === 0 ? "success" : succeeded === 0 ? "error" : "info";

  const total = items.length;
  const label = total === 1 ? "Document processed" : `${total} documents processed`;
  return { message: parts.length ? `${label} — ${parts.join(", ")}` : label, tone };
}

function captureMathWarning(e: any): string | null {
  if (e.status === "processing") return null;
  if (!totalsReconcile(e)) return "Subtotal + VAT doesn't match the total shown on the document.";
  if (!lineItemsReconcile(e)) return "Line items don't add up to the subtotal shown on the document.";
  return null;
}

// Exported so other tabs/components needing the same category list (e.g. the Insights
// tab's budget picker) import this one instead of keeping their own copy in sync by hand.
export const CATEGORIES = [
  "Office Supplies", "Travel & Transportation", "Meals & Entertainment", "Utilities",
  "Rent", "Software & Subscriptions", "Marketing & Advertising", "Professional Services",
  "Equipment", "Other",
];

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  processing:         { label: "Processing…",      bg: "#eff6ff", text: "#1d4ed8", border: "#93c5fd" },
  ready_to_confirm:   { label: "Ready to confirm",  bg: "#dcfce7", text: "#166534", border: "#86efac" },
  needs_review:       { label: "Needs review",      bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  confirmed:          { label: "Confirmed",         bg: "#dcfce7", text: "#166534", border: "#86efac" },
  rejected:           { label: "Rejected",          bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  extraction_failed:  { label: "Couldn't read this", bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
  discarded:          { label: "Discarded",         bg: "#f1f5f9", text: "#475569", border: "#e2e8f0" },
};
const PURPLE = { bg: "#ede9fe", text: "#6d28d9", border: "#c4b5fd" };

const CURRENCIES = ["THB", "INR", "USD", "EUR", "SGD"];

const DOC_CLASSES: Record<string, string> = {
  full_tax_invoice:        "ใบกำกับภาษี (Full Tax Invoice)",
  abbreviated_tax_invoice: "ใบกำกับภาษีอย่างย่อ (Abbreviated Tax Invoice)",
  delivery_note:           "ใบส่งของ (Delivery Note — not an expense)",
  billing_note:            "ใบวางบิล (Billing Note)",
  receipt:                 "ใบเสร็จ/บิลเงินสด (Receipt)",
  not_an_expense:          "Not an expense",
};

const BLANK_FORM = {
  vendor_name: "", vendor_name_en: "", doc_number: "",
  expense_date: new Date().toISOString().split("T")[0],
  category: "Other", subtotal: "", vat_amount: "0", total: "", currency: "THB",
  vendor_tax_id: "", tax_branch_no: "", doc_class: "", notes: "",
  branch_id: null as number | null,
  // null = unanswered/ambiguous (matches the AI's own null-when-unsure convention) —
  // "" input value maps back to null so re-opening an unedited capture doesn't silently
  // default it to "no WHT" (C4-9).
  wht_applicable: null as boolean | null, wht_rate: "",
};

const TABS = ["capture", "ledger", "branches", "insights", "settings"] as const;
type TabId = (typeof TABS)[number];

const OPEN_STATUSES = new Set(["ready_to_confirm", "needs_review"]);
// Mirrors the backend's _EDITABLE_STATUSES — extraction_failed captures can be deleted
// (discard a bad scan) even though they're not "open" for confirm/reject.
const DELETABLE_STATUSES = new Set(["ready_to_confirm", "needs_review", "extraction_failed"]);
// Same set works as "can this row be checked for bulk selection" — every bulk action
// (confirm/reject/delete) individually enforces its own tighter rule server-side, this is
// just the union of statuses where at least one of the three could apply.
const SELECTABLE_STATUSES = DELETABLE_STATUSES;

const BULK_FAILURE_REASONS: Record<string, string> = {
  not_open: "no longer open for review",
  delivery_note: "delivery notes can't be confirmed",
  not_an_expense: "not classified as an expense — reject it instead",
  not_found: "no longer exists",
  not_editable: "can't be deleted in its current status",
  error: "an unexpected error occurred",
};

function ReceiptPanel({ previewUrl, mimeType, isMobile, onFile, isLoading }: {
  previewUrl: string | null; mimeType?: string | null; isMobile: boolean; onFile?: (file: File) => void;
  isLoading?: boolean;
}) {
  const [zoomed, setZoomed] = useState(false);
  const isPdf = mimeType === "application/pdf" || previewUrl?.toLowerCase().endsWith(".pdf");
  return (
    <div
      onDragOver={onFile ? e => e.preventDefault() : undefined}
      onDrop={onFile ? e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); } : undefined}
      style={{
        border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc",
        overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
        height: isMobile ? 250 : 420, touchAction: "pan-y pinch-zoom",
      }}>
      {isLoading && !previewUrl ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: "#94a3b8", fontSize: 13 }}>
          <Spinner size={24} />
          <span>Loading receipt…</span>
        </div>
      ) : previewUrl && isPdf ? (
        <iframe
          src={previewUrl}
          title="Receipt PDF"
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      ) : previewUrl ? (
        <>
          <img src={previewUrl} alt="Receipt" onClick={() => setZoomed(true)}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", cursor: "zoom-in" }} />
          {zoomed && (
            <div onClick={() => setZoomed(false)} style={{
              position: "fixed", inset: 0, background: "rgba(15,23,42,0.92)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 1000, cursor: "zoom-out", padding: 20,
            }}>
              <img src={previewUrl} alt="Receipt (zoomed)"
                style={{ maxWidth: "95%", maxHeight: "95%", objectFit: "contain", touchAction: "pinch-zoom" }} />
            </div>
          )}
        </>
      ) : (
        <div style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", padding: 20 }}>No document uploaded</div>
      )}
    </div>
  );
}

function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span style={{
      display: "inline-block", width: size, height: size, borderRadius: "50%",
      border: `2px solid #e2e8f0`, borderTopColor: "#1d4ed8",
      animation: "spin 0.75s linear infinite", verticalAlign: "middle",
    }} />
  );
}

// Inject CSS keyframe once
if (typeof document !== "undefined" && !document.getElementById("expense-ocr-styles")) {
  const style = document.createElement("style");
  style.id = "expense-ocr-styles";
  style.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(style);
}

export default function AIExpenseCapturePage() {
  const qc = useQueryClient();
  const confirmDialog = useConfirm();
  const { toast } = useToast();
  const { user } = useAuth();
  const canApprove = user?.role === "admin" || user?.role === "admin_assistant" || user?.is_super_admin;
  const isMobile = useIsMobile(768);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Tab lives in the URL path — the module sidebar is the switcher (spec
  // module-subpage-navs). Old `?tab=` bookmarks from the useUrlSyncedTab era
  // redirect to their path equivalent below.
  const [tab] = useRouteTab<TabId>(
    "/ai-expense-capture",
    { capture: "", ledger: "ledger", branches: "branches", insights: "insights", settings: "settings" },
    "capture",
  );
  const [legacyParams] = useSearchParams();
  const navigateForLegacy = useNavigate();
  useEffect(() => {
    const legacy = legacyParams.get("tab");
    if (legacy && legacy !== "capture" && (TABS as readonly string[]).includes(legacy)) {
      navigateForLegacy(`/ai-expense-capture/${legacy}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  // Seeded from ?status= so the module dashboard's stat cards can deep-link into an
  // already-filtered list and have the matching chip show as selected on arrival, rather
  // than landing on the unfiltered list and making the user re-pick the filter they just
  // clicked. Validated against the known statuses — an arbitrary URL value would otherwise
  // select no chip and silently return nothing.
  const [statusFilter, setStatusFilter] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("status") || "";
    return fromUrl in STATUS_CONFIG ? fromUrl : "";
  });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  // C5-45: Save used to be `disabled` whenever a required field was empty, so clicking it
  // did nothing at all and said nothing about why. These drive per-field messages instead,
  // following the same collectErrors pattern as ExpensesPage/LeaveManagementPage.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [splitMode, setSplitMode] = useState(false);
  const [splitRows, setSplitRows] = useState<{ branch_id: number | ""; amount: string }[]>([]);
  // Whether the capture being edited had splits when it was first opened — turning
  // splitMode off from true means "clear them", which a plain branch_id diff can't
  // express when the capture's branch_id was already null (as every split capture's is).
  const [hadSplitsOriginally, setHadSplitsOriginally] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [receiptMimeType, setReceiptMimeType] = useState<string | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set());
  const [lowConfidenceFields, setLowConfidenceFields] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [viewOnly, setViewOnly] = useState(false);
  // C5-30: a confirmed capture opens read-only, but its branch allocation stays
  // correctable (an allocation moves no money — see _SPLITTABLE_STATUSES server-side).
  // This mode keeps every money field locked and opens the Location block alone.
  const [splitsOnly, setSplitsOnly] = useState(false);
  const [editingStatus, setEditingStatus] = useState<string | null>(null);
  const [editingOriginalFilename, setEditingOriginalFilename] = useState<string | null>(null);
  const [decisionPending, setDecisionPending] = useState(false);

  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  // The batch_id of the upload currently being watched for its outcome (C5-17/C5-36).
  const [watchedBatchId, setWatchedBatchId] = useState<string | null>(null);

  // Bulk select actions (spec: expense-capture-bulk-select-actions)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkFailures, setBulkFailures] = useState<Record<number, string>>({});
  const [bulkPending, setBulkPending] = useState(false);

  // Table view: search/filter/sort/pagination (spec: expense-capture-table-view)
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [branchFilterId, setBranchFilterId] = useState<number | "">("");
  const [sortBy, setSortBy] = useState("");   // "" = default (needs_review-first, then date desc)
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 25;

  // Header/mobile redesign (spec: expense-capture-ui-redesign)
  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = [categoryFilter, dateFromFilter, dateToFilter, branchFilterId !== "" ? "x" : ""].filter(Boolean).length;
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Debounce the raw input so every keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any change to what's being asked for should land back on page 1 — staying on e.g.
  // page 4 after narrowing a search to 2 results would just show an empty page.
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, categoryFilter, dateFromFilter, dateToFilter, branchFilterId, search, sortBy, sortDir]);

  const { data: listResp, isLoading, isError } = useQuery({
    queryKey: ["expense-captures", statusFilter, categoryFilter, dateFromFilter, dateToFilter, branchFilterId, search, sortBy, sortDir, currentPage],
    queryFn: () => getExpenseCaptures({
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(categoryFilter ? { category: categoryFilter } : {}),
      ...(dateFromFilter ? { date_from: dateFromFilter } : {}),
      ...(dateToFilter ? { date_to: dateToFilter } : {}),
      ...(branchFilterId !== "" ? { branch_id: branchFilterId as number } : {}),
      ...(search ? { search } : {}),
      ...(sortBy ? { sort_by: sortBy, sort_dir: sortDir } : {}),
      page: currentPage, page_size: PAGE_SIZE,
      watching: true,
    }),
    refetchOnWindowFocus: false,
  });

  const captures: any[] = listResp?.items ?? [];
  const hasPending: boolean = listResp?.has_pending ?? false;
  const totalCount: number = listResp?.total ?? 0;
  const totalPages: number = listResp?.total_pages ?? 1;

  function toggleSort(column: string) {
    if (sortBy === column) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(column); setSortDir("asc"); }
  }

  const { data: branchesConfig } = useQuery({ queryKey: ["branches-config"], queryFn: getBranchesConfig });
  const branchesEnabled = !!branchesConfig?.enabled;
  const { data: branchesResp } = useQuery({ queryKey: ["branches"], queryFn: getBranches, enabled: branchesEnabled });
  const activeBranches = (branchesResp?.branches ?? []).filter(b => b.status === "active");
  // The location allocation has its own editability, separate from the rest of the form:
  // a confirmed capture is read-only everywhere else but its branch split stays
  // correctable (C5-30).
  const canEditSplits = !viewOnly || splitsOnly;

  // Contextual "Confirm all high-confidence" (spec expense-capture-ui-redesign AC4/AC8) —
  // a tiny page_size=1 call just to read `total`, cheaper than fetching the full list a
  // second time. Approvers only; non-approvers never see the confirm-all affordance anyway.
  const { data: rtcResp } = useQuery({
    queryKey: ["expense-captures", "rtc-count"],
    queryFn: () => getExpenseCaptures({ status: "ready_to_confirm", page: 1, page_size: 1 }),
    enabled: canApprove,
  });
  const readyToConfirmCount = rtcResp?.total ?? 0;

  // Polling while any capture is still being extracted
  useEffect(() => {
    if (!hasPending) return;
    const id = setInterval(() => {
      qc.invalidateQueries({ queryKey: ["expense-captures"] });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasPending, qc]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["expense-captures"] });

  // ── Upload outcome watcher (C5-17 / C5-36) ─────────────────────────────────
  // Uploading showed "Queued for processing" and then went silent forever: the row
  // updated in place if you happened to still be looking at it, and nothing was reported
  // when extraction finished, failed, or discarded the document.
  //
  // Worse, the row often wasn't there to watch. A processing capture is seeded with
  // expense_date = today, and the default sort is needs_review-first then date descending
  // — so the moment extraction writes the document's real (usually older) date, a capture
  // that didn't land in needs_review re-sorts down the list and, at PAGE_SIZE 25, straight
  // off page 1. To the user the receipt simply vanished.
  //
  // So the outcome is read from the batch, not the table. scan-async already accepts and
  // returns a batch_id and the list endpoint already filters by it, so this needs no new
  // API surface — and it reports on exactly the files this user just uploaded rather than
  // on whatever else the tenant happens to be processing.
  const { data: watchedBatch } = useQuery({
    queryKey: ["expense-capture-batch", watchedBatchId],
    queryFn: () => getExpenseCaptures({ batch_id: watchedBatchId as string, page: 1, page_size: MAX_BATCH_FILES }),
    enabled: !!watchedBatchId,
    refetchInterval: watchedBatchId ? POLL_INTERVAL_MS : false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!watchedBatchId) return;
    const items: any[] = watchedBatch?.items ?? [];
    // No rows yet — the read raced the write. Keep waiting rather than reporting "0 done".
    if (!items.length) return;
    if (items.some(i => i.status === "processing")) return;

    // Terminates without a timeout of its own: a capture whose worker died is swept to
    // extraction_failed after _STALE_PROCESSING_MINUTES, and the sweep runs on every list
    // call — including this one. So the watch always resolves rather than polling forever.
    const { message, tone } = summarizeBatchOutcome(items);
    toast(message, tone);
    setWatchedBatchId(null);
    invalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedBatch, watchedBatchId]);

  // C5-29: these deliberately do NOT close the form. The save is a two-step operation —
  // the record, then its location split — and closing on the first step's success tore the
  // modal down while the second was still in flight, so a split error surfaced as a toast
  // over the list with the user's rows already discarded. handleSubmit closes explicitly
  // once every step has landed.
  const createMut  = useMutation({ mutationFn: createExpenseCapture,  onSuccess: invalidate });
  const updateMut  = useMutation({ mutationFn: ({ id, data }: { id: number; data: FormData }) => updateExpenseCapture(id, data), onSuccess: invalidate });
  const deleteMut  = useMutation({ mutationFn: deleteExpenseCapture,  onSuccess: invalidate });
  const confirmMut = useMutation({ mutationFn: confirmExpenseCapture, onSuccess: invalidate });
  const rejectMut  = useMutation({ mutationFn: rejectExpenseCapture,  onSuccess: invalidate });
  const reprocessMut = useMutation({ mutationFn: reprocessExpenseCapture, onSuccess: invalidate });
  const resolveDupMut = useMutation({
    mutationFn: ({ id, action }: { id: number; action: "keep_both" | "discard" }) => resolveDuplicateExpenseCapture(id, action),
    onSuccess: invalidate,
  });

  function toggleSelected(id: number) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // Shared runner for all three bulk actions — applies the API call to the current
  // selection, reports a result-summary toast, keeps per-capture failure reasons around so
  // the still-visible rows can show why they weren't touched (AC6), and clears the
  // selection + refreshes the list on completion (AC8).
  async function runBulkAction(
    label: string,
    fn: (ids: number[]) => Promise<BulkActionResult>,
  ) {
    const ids = Array.from(selectedIds) as number[];
    if (!ids.length) return;
    setBulkPending(true);
    try {
      const { results } = await fn(ids);
      const succeeded = results.filter(r => r.ok).length;
      const failed = results.filter(r => !r.ok);
      const failureMap: Record<number, string> = {};
      failed.forEach(r => { failureMap[r.id] = r.reason || "error"; });
      setBulkFailures(failureMap);
      toast(
        failed.length
          ? `${succeeded} ${label}, ${failed.length} failed — see reasons below`
          : `${succeeded} ${label}`,
        failed.length ? "error" : "success",
      );
      setSelectedIds(new Set());
      invalidate();
    } catch (err: any) {
      toast(getErrorMessage(err, `Could not ${label.replace(/s$/, "")} the selected captures`), "error");
    } finally {
      setBulkPending(false);
    }
  }

  const handleBulkConfirm = () => runBulkAction("confirmed", bulkActionConfirmExpenseCaptures);
  const handleBulkReject = () => runBulkAction("rejected", bulkActionRejectExpenseCaptures);
  async function handleBulkDelete() {
    if (!(await confirmDialog(`Delete ${selectedIds.size} selected capture${selectedIds.size !== 1 ? "s" : ""}? This cannot be undone.`, { confirmLabel: "Delete" })))
      return;
    await runBulkAction("deleted", bulkActionDeleteExpenseCaptures);
  }

  function closeForm() {
    setShowForm(false); setForm(BLANK_FORM); setFieldErrors({});
    setEditingId(null); setViewOnly(false); setSplitsOnly(false);
    setEditingStatus(null); setEditingOriginalFilename(null);
    setReceiptFile(null);
    if (receiptPreview?.startsWith("blob:")) URL.revokeObjectURL(receiptPreview);
    setReceiptPreview(null); setReceiptMimeType(null); setReceiptLoading(false);
    setAiFilledFields(new Set()); setLowConfidenceFields(new Set());
    setSplitMode(false); setSplitRows([]); setHadSplitsOriginally(false);
  }

  function selectReceiptFile(file: File) {
    setReceiptFile(file);
    setReceiptMimeType(file.type || null);
    if (receiptPreview?.startsWith("blob:")) URL.revokeObjectURL(receiptPreview);
    if (file.type.startsWith("image/") || file.type === "application/pdf") {
      setReceiptPreview(URL.createObjectURL(file));
    } else {
      setReceiptPreview(null);
    }
  }

  async function loadReceiptPreview(id: number) {
    setReceiptLoading(true);
    try {
      const blob = await fetchExpenseCaptureReceiptBlob(id);
      setReceiptPreview(prev => { if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    } catch { /* no receipt */ }
    finally { setReceiptLoading(false); }
  }

  function clearFieldError(field: string) {
    setFieldErrors(prev => {
      if (!(field in prev)) return prev;   // no-op keeps the object identity stable
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  }

  function clearAiMark(field: string) {
    setAiFilledFields(prev => { const n = new Set(prev); n.delete(field); return n; });
    setLowConfidenceFields(prev => { const n = new Set(prev); n.delete(field); return n; });
    // Every edited field already routes through here, so a stale "required" message
    // clears the moment the user types rather than lingering until the next submit.
    clearFieldError(field);
  }

  // C5-45 + C5-25 + C5-26. Mirrors the server-side rules (validation.py / expense_capture
  // .py) so the form never accepts something the API will reject.
  function validateForm(): Record<string, string> {
    return collectErrors({
      vendor_name: requiredTrimmed(form.vendor_name, "Vendor name"),
      expense_date: requiredValue(form.expense_date, "Date")
        || notFutureDate(form.expense_date, "Date"),
      category: requiredValue(form.category, "Category"),
      // Required, but 0 is a legitimate value — matching the backend, which only rejects
      // negatives. `requiredValue("0")` passes because "0" is a non-empty string.
      subtotal: requiredValue(form.subtotal, "Subtotal"),
      // C5-25: answering "Yes — service" without a rate stored the capture flagged for
      // WHT with a NULL rate and NULL amount, so it sat in the Ledger's "WHT pending"
      // list contributing nothing to "WHT to issue".
      wht_rate: form.wht_applicable === true
        ? requiredValue(form.wht_rate, "WHT rate")
        : null,
    });
  }

  // ── Legacy sync scan (existing form "Scan Receipt" button) ────────────────
  async function handleScanReceipt(file: File) {
    setShowForm(true); selectReceiptFile(file); setScanning(true);
    let result: any;
    try { result = await scanExpenseCaptureReceipt(file); }
    catch (e: any) {
      // bug-85: surface the backend's specific "not an expense document" rejection
      // instead of the generic unreadable-document message, so the user understands
      // why the scan was refused rather than assuming it's a scan-quality problem.
      toast(e?.response?.data?.detail || "Could not read this document — please fill in manually", "error");
      return;
    }
    finally { setScanning(false); }

    const filled = new Set<string>();
    setForm(f => {
      const next = { ...f };
      if (result.vendor_name)  { next.vendor_name  = result.vendor_name;  filled.add("vendor_name"); }
      if (result.vendor_name_en) { next.vendor_name_en = result.vendor_name_en; filled.add("vendor_name_en"); }
      if (result.doc_number) { next.doc_number = result.doc_number; filled.add("doc_number"); }
      if (result.expense_date) { next.expense_date = result.expense_date; filled.add("expense_date"); }
      if (result.subtotal != null) { next.subtotal = String(result.subtotal); filled.add("subtotal"); }
      if (result.vat_amount != null) { next.vat_amount = String(result.vat_amount); filled.add("vat_amount"); }
      // Bug #87: seed Total from the AI's own extracted value (not derived from
      // subtotal+VAT) — it's now independently editable, so the user needs to see and be
      // able to correct exactly what the AI read, not a value the form invented for them.
      if (result.total != null) { next.total = String(result.total); filled.add("total"); }
      if (result.category && CATEGORIES.includes(result.category)) { next.category = result.category; filled.add("category"); }
      if (result.currency && CURRENCIES.includes(result.currency)) { next.currency = result.currency; filled.add("currency"); }
      if (result.vendor_tax_id) { next.vendor_tax_id = result.vendor_tax_id; filled.add("vendor_tax_id"); }
      if (result.tax_branch_no) { next.tax_branch_no = result.tax_branch_no; filled.add("tax_branch_no"); }
      if (result.doc_class && DOC_CLASSES[result.doc_class]) { next.doc_class = result.doc_class; filled.add("doc_class"); }
      return next;
    });
    setAiFilledFields(filled);
    const conf = result.field_confidence || {};
    const low = new Set<string>();
    filled.forEach(f => { if (typeof conf[f] === "number" && conf[f] < HIGH_CONFIDENCE) low.add(f); });
    setLowConfidenceFields(low);
    toast("Document scanned — please review the highlighted fields before saving", "success");
  }

  // ── Async multi-file upload ────────────────────────────────────────────────
  const handleAsyncUpload = useCallback(async (fileList: FileList | File[], source?: string) => {
    const files = Array.from(fileList);
    if (!files.length) return;
    if (files.length > MAX_BATCH_FILES) {
      toast(`Maximum ${MAX_BATCH_FILES} files per upload`, "error");
      return;
    }

    const batchId = crypto.randomUUID();
    setUploadProgress({ done: 0, total: files.length });

    try {
      await scanExpenseCaptureReceiptAsync(files, batchId, source);
      setUploadProgress(null);
      await invalidate();
      // C5-36: watch this specific batch so the outcome can be reported when extraction
      // finishes. Watching the batch rather than the visible list matters — a finished
      // capture frequently isn't in the list any more (see the C5-17 note on the batch
      // watcher below), so "did it work?" cannot be answered from the table alone.
      setWatchedBatchId(batchId);
      toast(
        files.length === 1
          ? "Queued for processing — it'll appear in the list when ready"
          : `${files.length} documents queued — cards will update as each finishes`,
        "success",
      );
    } catch (err: any) {
      setUploadProgress(null);
      toast(getErrorMessage(err, "Upload failed — please try again"), "error");
    }
  }, [toast, invalidate]);

  // Derived subtotal+VAT sum — shown only as a cross-check hint next to the (now
  // independently editable) Total field; no longer what gets saved as the total itself.
  const calcTotal = () => (parseFloat(form.subtotal) || 0) + (parseFloat(form.vat_amount) || 0);
  // The actual total to save/validate splits against: the user's (or AI's) entered value,
  // falling back to the derived subtotal+VAT sum only when Total has never been touched
  // (e.g. a brand-new manual entry with no receipt) — matches the backend's own fallback
  // in _calc_total when `total` is omitted from the request.
  const enteredTotal = () => { const t = parseFloat(form.total); return Number.isFinite(t) ? t : calcTotal(); };

  function buildFormData(force = false): FormData {
    const fd = new FormData();
    fd.append("vendor_name", form.vendor_name);
    if (form.vendor_name_en) fd.append("vendor_name_en", form.vendor_name_en);
    if (form.doc_number) fd.append("doc_number", form.doc_number);
    fd.append("expense_date", form.expense_date);
    fd.append("category", form.category);
    fd.append("subtotal", form.subtotal);
    fd.append("vat_amount", form.vat_amount || "0");
    // Omit entirely when blank so the backend keeps deriving from subtotal+VAT (its own
    // default) rather than rejecting an empty string as an invalid float.
    if (form.total !== "") fd.append("total", form.total);
    fd.append("currency", form.currency || "THB");
    if (form.vendor_tax_id) fd.append("vendor_tax_id", form.vendor_tax_id);
    if (form.tax_branch_no) fd.append("tax_branch_no", form.tax_branch_no);
    if (form.doc_class) fd.append("doc_class", form.doc_class);
    // Split mode sends no single branch_id — the splits endpoint call (after save) sets
    // the per-branch breakdown instead, and the backend clears one when the other is set.
    if (!splitMode && form.branch_id != null) fd.append("branch_id", String(form.branch_id));
    // A split capture's branch_id is already null, so turning split mode off without
    // picking a new branch wouldn't otherwise look like a change to the backend — tell it
    // explicitly to clear the stale split rows.
    if (!splitMode && hadSplitsOriginally) fd.append("clear_splits", "true");
    if (form.notes) fd.append("notes", form.notes);
    // Only send a WHT decision once the reviewer has actually answered — null stays
    // untouched server-side rather than getting coerced into "no WHT" (C4-9).
    if (form.wht_applicable !== null) {
      fd.append("wht_applicable", String(form.wht_applicable));
      if (form.wht_rate !== "") fd.append("wht_rate", form.wht_rate);
    }
    if (force) fd.append("force", "true");
    if (receiptFile) fd.append("receipt", receiptFile);
    return fd;
  }

  // Running total of the location rows — surfaced live in the editor (C5-30: QA's
  // "Branches — total" was a request to see the accumulated allocation while entering it,
  // not just a pass/fail after saving).
  const splitAllocated = () =>
    splitRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  // Flat shape rather than a discriminated union on `ok` — this project's tsconfig doesn't
  // narrow such a union at the call sites, and this needs no narrowing to read:
  // `error` set = don't save; `rows` null with no error = nothing to allocate.
  type SplitCheck = { error: string | null; rows: { branch_id: number; amount: number }[] | null };

  // Pure check — returns a verdict instead of toasting, so the caller decides whether a
  // failure should abort the whole save (it should) rather than being told after the fact.
  function checkSplitRows(): SplitCheck {
    const rows = splitRows.filter(r => r.branch_id !== "" && r.amount !== "");
    if (!rows.length) return { error: null, rows: null };
    const total = enteredTotal();
    const sum = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    if (Math.abs(sum - total) > 0.01) {
      return {
        error: `Locations add up to ${sum.toFixed(2)} but the total is ${total.toFixed(2)} — adjust the amounts so they match.`,
        rows: null,
      };
    }
    return { error: null, rows: rows.map(r => ({ branch_id: Number(r.branch_id), amount: parseFloat(r.amount) })) };
  }

  // The Branches tab reports confirmed spend only, so a split entered during review is
  // saved correctly but stays invisible until someone confirms the capture. Saying so
  // out loud is what stops it reading as "the split didn't save" (C5-30).
  function toastSplitSaved() {
    toast(
      editingStatus === "confirmed"
        ? "Location split updated"
        : "Location split saved — it appears on the Branches tab once this expense is confirmed",
      "success",
    );
  }

  async function handleSubmit() {
    // C5-45: this used to be a bare `return` behind a disabled button — the click did
    // nothing and named no field. Now the offending fields say so.
    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    // C5-29: the split is validated BEFORE anything is written. The old order saved the
    // record first and only then discovered the mismatch — by which point the modal had
    // already closed, so the error landed on the list page and the rows were lost.
    let splitPayload: { branch_id: number; amount: number }[] | null = null;
    if (splitMode) {
      const check = checkSplitRows();
      if (check.error) { toast(check.error, "error"); return; }
      splitPayload = check.rows;
    }

    // Record, then split, then close — one path for create and edit so the two can't
    // drift apart on ordering or on how a duplicate retry re-runs them.
    async function saveAll(force: boolean) {
      let captureId: number;
      if (editingId != null) {
        await updateMut.mutateAsync({ id: editingId, data: buildFormData(force) });
        captureId = editingId;
      } else {
        captureId = (await createMut.mutateAsync(buildFormData(force))).id;
      }
      if (splitPayload) {
        try {
          await setExpenseCaptureSplits(captureId, splitPayload);
        } catch (err: any) {
          // The record itself is saved; only the split failed. Keep the modal open so the
          // rows can be corrected rather than re-entered from scratch.
          toast(getErrorMessage(err, "The expense was saved, but its location split was not"), "error");
          invalidate();
          return;
        }
        toastSplitSaved();
      }
      invalidate();
      closeForm();
    }

    try {
      await saveAll(false);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "duplicate_suspected") {
        if (await confirmDialog(`${detail.message}\n\nSave anyway?`, { confirmLabel: "Save anyway" })) {
          try {
            await saveAll(true);
          } catch (retryErr: any) {
            toast(getErrorMessage(retryErr, "Could not save expense capture — please try again"), "error");
          }
        }
        return;
      }
      if (isStaleStatusConflict(err)) { resyncAfterStatusConflict(); return; }
      toast(getErrorMessage(err, "Could not save expense capture — please try again"), "error");
    }
  }

  // C5-18. Every write path here re-checks the capture's status server-side, so a row that
  // changed since the list was last fetched fails with a 409 carrying a message written for
  // an API client: "Cannot edit a capture with status 'confirmed'". The user is told the
  // record is in a state their screen does not show — and the screen goes on showing the
  // old state, so retrying reproduces it exactly.
  //
  // A status conflict is not really an error to report; it is a signal that this view is
  // stale. Say that plainly and resync, so the next attempt starts from what is stored.
  function isStaleStatusConflict(err: any): boolean {
    if (err?.response?.status !== 409) return false;
    const detail = err?.response?.data?.detail;
    return typeof detail === "string" && detail.startsWith("Cannot edit a capture with status");
  }

  function resyncAfterStatusConflict() {
    toast(
      "This capture changed elsewhere and can no longer be edited — the list has been refreshed",
      "info",
    );
    invalidate();
    closeForm();
  }

  // Split-only save for an already-confirmed capture (C5-30) — never touches the record
  // itself, so it can't re-trip the duplicate guard on fields nobody edited.
  async function handleSaveSplitsOnly() {
    if (editingId == null) return;
    const check = checkSplitRows();
    if (check.error) { toast(check.error, "error"); return; }
    if (!check.rows) { toast("Add at least one location before saving", "error"); return; }
    setDecisionPending(true);
    try {
      await setExpenseCaptureSplits(editingId, check.rows);
      toastSplitSaved();
      invalidate();
      closeForm();
    } catch (err: any) {
      if (isStaleStatusConflict(err)) { resyncAfterStatusConflict(); return; }
      toast(getErrorMessage(err, "Could not save the location split"), "error");
    } finally {
      setDecisionPending(false);
    }
  }

  // Confirm/reject directly from the review screen — no detour back to the card list.
  async function handleConfirmInReview() {
    if (editingId == null) return;
    // Confirming writes the record first, so it must clear the same bar as saving —
    // otherwise Confirm becomes a way around the validation Save enforces.
    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    // Same pre-validation as handleSubmit (C5-29) — catching the mismatch here means the
    // capture isn't confirmed with an allocation that was silently dropped.
    let splitPayload: { branch_id: number; amount: number }[] | null = null;
    if (splitMode) {
      const check = checkSplitRows();
      if (check.error) { toast(check.error, "error"); return; }
      splitPayload = check.rows;
    }
    setDecisionPending(true);
    try {
      await updateExpenseCapture(editingId, buildFormData());
      if (splitPayload) await setExpenseCaptureSplits(editingId, splitPayload);
      await confirmExpenseCapture(editingId);
      invalidate();
      closeForm();
      toast("Expense confirmed", "success");
    } catch (err: any) {
      if (isStaleStatusConflict(err)) { resyncAfterStatusConflict(); return; }
      toast(getErrorMessage(err, "Could not confirm — please try again"), "error");
    } finally {
      setDecisionPending(false);
    }
  }

  async function handleRejectInReview() {
    if (editingId == null) return;
    setDecisionPending(true);
    try {
      await rejectExpenseCapture(editingId);
      invalidate();
      closeForm();
      toast("Expense rejected", "success");
    } catch (err: any) {
      toast(getErrorMessage(err, "Could not reject — please try again"), "error");
    } finally {
      setDecisionPending(false);
    }
  }

  function openExisting(e: any, readOnly: boolean, opts: { splitsOnly?: boolean } = {}) {
    if (e.status === "processing") return; // card is locked while extraction runs
    setEditingId(e.id); setViewOnly(readOnly); setSplitsOnly(!!opts.splitsOnly);
    setEditingStatus(e.status || null);
    setEditingOriginalFilename(e.original_filename || null);
    setForm({
      vendor_name: e.vendor_name || "", vendor_name_en: e.vendor_name_en || "",
      doc_number: e.doc_number || "",
      expense_date: e.expense_date || new Date().toISOString().split("T")[0],
      category: e.category || "Other", subtotal: String(e.subtotal ?? ""), vat_amount: String(e.vat_amount ?? "0"),
      // Bug #87: seed from the capture's actual stored total, not a derived subtotal+VAT
      // sum — otherwise re-opening and saving without touching Total would silently
      // overwrite a legitimately-different total (service charge/discount/rounding) back
      // to subtotal+VAT.
      total: String(e.total ?? ""),
      currency: e.currency || "THB", vendor_tax_id: e.vendor_tax_id || "",
      tax_branch_no: e.tax_branch_no || "", doc_class: e.doc_class || "",
      notes: e.notes || "", branch_id: e.branch_id ?? null,
      wht_applicable: e.wht_applicable ?? null, wht_rate: e.wht_rate != null ? String(e.wht_rate) : "",
    });
    setReceiptFile(null);
    if (receiptPreview?.startsWith("blob:")) URL.revokeObjectURL(receiptPreview);
    setReceiptPreview(null); setReceiptMimeType(e.receipt_mime_type || null); setReceiptLoading(false);
    setAiFilledFields(new Set()); setLowConfidenceFields(new Set());
    // Highlight AI-extracted fields (yellow) and low-confidence ones (orange)
    if (e.field_confidence) {
      const conf: Record<string, number> = e.field_confidence;
      const allAiFilled = new Set<string>(Object.keys(conf));
      setAiFilledFields(allAiFilled);
      setLowConfidenceFields(new Set(Object.keys(conf).filter(f => conf[f] < HIGH_CONFIDENCE)));
    }
    // Splits-only mode is entered specifically to allocate, so open the editor immediately
    // with one row covering the whole total rather than waiting on the fetch below — the
    // real rows overwrite this if the capture already has any, and a failed fetch leaves a
    // usable editor instead of a disabled single-branch select with no way forward.
    setSplitMode(!!opts.splitsOnly);
    setSplitRows(opts.splitsOnly ? [{ branch_id: "", amount: String(e.total ?? "") }] : []);
    setHadSplitsOriginally(false);
    if (branchesEnabled) {
      getExpenseCaptureSplits(e.id).then(({ splits }) => {
        if (splits.length) {
          setSplitMode(true);
          setHadSplitsOriginally(true);
          setSplitRows(splits.map(s => ({ branch_id: s.branch_id, amount: String(s.amount) })));
        }
      }).catch(() => {});
    }
    setShowForm(true);
    if (e.has_receipt) loadReceiptPreview(e.id);
  }

  const startEdit = (e: any) => openExisting(e, false);
  const startView = (e: any) => openExisting(e, true);
  // Read-only on every money field, editable on the location allocation alone (C5-30).
  const startEditLocations = (e: any) => openExisting(e, true, { splitsOnly: true });

  // Deep link from the module dashboard's review queue: ?capture=<id> opens that capture's
  // review form directly. Fetched by id rather than looked up in the loaded list — the
  // queue surfaces the longest-waiting captures, which are precisely the ones least likely
  // to be on page 1 of a newest-first list. Runs once on mount; the param is then dropped
  // so that closing the form and refreshing doesn't silently reopen it.
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get("capture");
    if (!raw) return;
    const id = Number(raw);
    if (!Number.isInteger(id) || id <= 0) return;
    let cancelled = false;
    getExpenseCapture(id)
      .then(capture => { if (!cancelled && capture) openExisting(capture, false); })
      .catch(() => { if (!cancelled) toast("That capture could not be opened", "error"); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bug #4: the duplicate warning links to the other capture instead of showing only its
  // internal id — fetch it fresh since it may not be in the currently filtered list.
  async function viewDuplicateOf(id: number) {
    try {
      const capture = await getExpenseCapture(id);
      startView(capture);
    } catch {
      toast("Could not load the original capture — it may have been deleted", "error");
    }
  }

  async function handleDelete(id: number) {
    if (await confirmDialog("Delete this expense capture? This cannot be undone.", { confirmLabel: "Delete" }))
      deleteMut.mutate(id);
  }

  async function handleExportCsv() {
    setExportingCsv(true);
    try {
      const { blob, filename } = await downloadExpenseCapturesExport(statusFilter ? { status: statusFilter } : undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast("Failed to export CSV", "error"); }
    finally { setExportingCsv(false); }
  }

  async function handleExportPdf() {
    setExportingPdf(true);
    try {
      const { blob, filename } = await downloadExpenseCapturesPdf(statusFilter ? { status: statusFilter } : undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast("Failed to export PDF", "error"); }
    finally { setExportingPdf(false); }
  }

  function aiStyle(field: string): React.CSSProperties {
    if (!aiFilledFields.has(field)) return inputStyle;
    if (lowConfidenceFields.has(field)) return { ...inputStyle, background: "#fff7ed", borderColor: "#fb923c" };
    return { ...inputStyle, background: "#fefce8", borderColor: "#fde047" };
  }

  // ── Card rendering ─────────────────────────────────────────────────────────
  // Shared between the desktop table row and the mobile stacked card (spec:
  // expense-capture-table-view AC9 — mobile gets the same feature set, just a different
  // presentation) so the two layouts can't silently drift apart on what a capture shows.
  function renderCaptureBadges(e: any, opts: { includeStatus?: boolean } = {}) {
    const { includeStatus = true } = opts;
    const processing = e.status === "processing";
    const isAutoConfirmed = e.confirmed_by === "system:ocr_auto_confirm";
    const sc = STATUS_CONFIG[e.status] || STATUS_CONFIG.needs_review;
    const isDeliveryNote = e.doc_class === "delivery_note";
    return (
      <>
        {includeStatus && (
          <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
            {sc.label}
          </span>
        )}
        {isAutoConfirmed && (
          <span style={{ background: STATUS_CONFIG.confirmed.bg, color: STATUS_CONFIG.confirmed.text, padding: "2px 10px", borderRadius: 20, fontSize: 11 }} title="Confirmed automatically — every field was high-confidence, math reconciled, and a known vendor">
            ✓ Auto-confirmed
          </span>
        )}
        {e.wht_applicable === null && !processing && (
          <span style={{ background: PURPLE.bg, color: PURPLE.text, padding: "2px 10px", borderRadius: 20, fontSize: 11 }} title="Could be goods or services — WHT never auto-applied when ambiguous">
            ⚠ WHT — service or goods?
          </span>
        )}
        {isDeliveryNote && !processing && (
          <span style={{ background: "#f1f5f9", color: "#475569", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }} title="Delivery notes are linked to the vendor for reference but are never booked as an expense">
            📦 Delivery note — not an expense
          </span>
        )}
        {e.branch_name && !processing && (
          <span style={{ background: "#eff6ff", color: "#1d4ed8", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>
            📍 {e.branch_name}
          </span>
        )}
        {e.has_receipt && !processing && <span title="Document attached" style={{ fontSize: 13 }}>📎</span>}
      </>
    );
  }

  // `inlineDuplicateActions` splits the two layouts (C5-04). The mobile card already
  // carries its actions inline, so Discard / Keep both belong with them there. The desktop
  // table puts every other action behind the row's ⋮ menu, and these two were the lone
  // exception — a pair of buttons rendered inside the table body, in a full-width sub-row
  // between data rows. That is what read as cluttered and overlapping. The warning text
  // itself stays inline in both: it is context, not an action.
  function renderCaptureWarnings(e: any, opts: { inlineDuplicateActions?: boolean } = {}) {
    const isDuplicate = e.duplicate_status === "possible";
    const mathWarning = captureMathWarning(e);
    return (
      <>
        {e.status === "extraction_failed" && (
          <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>
            Couldn't read this{e.original_filename ? ` (${e.original_filename})` : ""} — {e.extraction_error || "unknown error"}
          </div>
        )}
        {/* C5-17: `discarded` is what the pipeline assigns to a document it classified as
            not an expense. It is terminal and has no review queue, so without this the row
            carried a bare "Discarded" badge and no hint as to what happened or why. */}
        {e.status === "discarded" && (
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
            Not an expense document{e.original_filename ? ` (${e.original_filename})` : ""} — this was read as
            something other than a receipt, invoice, or bill, so it was never booked as an expense.
          </div>
        )}
        {mathWarning && (
          <div style={{ fontSize: 12, color: "#c2410c", marginTop: 6 }}>
            ⚠ {mathWarning}
          </div>
        )}
        {e.status === "processing" && e.original_filename && (
          <div style={{ fontSize: 12, color: "#1d4ed8", marginTop: 4 }}>{e.original_filename}</div>
        )}
        {isDuplicate && (
          <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span>
              ⚠ Possible duplicate of{" "}
              {e.duplicate_of_vendor_name ? (
                <button type="button" onClick={() => viewDuplicateOf(e.duplicate_of_id)}
                  style={{ background: "none", border: "none", padding: 0, color: "#dc2626", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>
                  #{e.duplicate_of_id} "{e.duplicate_of_vendor_name}"{e.duplicate_of_expense_date ? ` on ${e.duplicate_of_expense_date}` : ""}
                  {e.duplicate_of_total != null ? ` (${e.currency || "THB"} ${e.duplicate_of_total.toLocaleString("en", { minimumFractionDigits: 2 })})` : ""}
                </button>
              ) : `capture #${e.duplicate_of_id}`}
            </span>
            {opts.inlineDuplicateActions && (
              <>
                <AsyncButton onClick={() => resolveDupMut.mutateAsync({ id: e.id, action: "discard" })}
                  style={{ ...outlineBtnStyle, color: "#dc2626", borderColor: "#fecaca", padding: "3px 10px", fontSize: 11 }}>
                  Discard
                </AsyncButton>
                <AsyncButton onClick={() => resolveDupMut.mutateAsync({ id: e.id, action: "keep_both" })}
                  style={{ ...outlineBtnStyle, padding: "3px 10px", fontSize: 11 }}>
                  Keep both
                </AsyncButton>
              </>
            )}
          </div>
        )}
        {e.paired_delivery_note_ids?.length > 0 && (
          <div style={{ fontSize: 12, color: "#854d0e", marginTop: 6 }}>
            May include {e.paired_delivery_note_ids.length} delivery note{e.paired_delivery_note_ids.length !== 1 ? "s" : ""} already recorded for this vendor
          </div>
        )}
        {bulkFailures[e.id] && (
          <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>
            ⚠ Bulk action failed — {BULK_FAILURE_REASONS[bulkFailures[e.id]] || bulkFailures[e.id]}
          </div>
        )}
      </>
    );
  }

  // Mobile inline actions. Duplicate resolution is NOT here — the mobile card keeps
  // Discard / Keep both inline with the warning text itself (see renderCaptureWarnings'
  // inlineDuplicateActions), which is where they read naturally on a stacked card.
  function renderCaptureActions(e: any) {
    const processing = e.status === "processing";
    const canDecide = canApprove && OPEN_STATUSES.has(e.status);
    const isDeliveryNote = e.doc_class === "delivery_note";
    return (
      <>
        {processing && <span style={{ fontSize: 12, color: "#1d4ed8" }}>Extracting in background…</span>}
        {e.status === "extraction_failed" && (
          <button onClick={() => startEdit(e)} style={{ ...outlineBtnStyle, color: "#dc2626", borderColor: "#fca5a5" }}>
            Enter manually
          </button>
        )}
        {canDecide && (
          <>
            <button onClick={() => startEdit(e)} style={outlineBtnStyle}>Review</button>
            {!isDeliveryNote && (
              <AsyncButton onClick={() => confirmMut.mutateAsync(e.id)} style={{ ...outlineBtnStyle, color: "#16a34a", borderColor: "#86efac", fontWeight: 600 }}>Confirm</AsyncButton>
            )}
            <AsyncButton onClick={() => rejectMut.mutateAsync(e.id)} style={{ ...outlineBtnStyle, color: "#dc2626", borderColor: "#fca5a5" }}>Reject</AsyncButton>
          </>
        )}
        {!canDecide && OPEN_STATUSES.has(e.status) && (
          <button onClick={() => startEdit(e)} style={outlineBtnStyle}>Edit</button>
        )}
        {!processing && !OPEN_STATUSES.has(e.status) && e.status !== "extraction_failed" && (
          <>
            <button onClick={() => startView(e)} style={outlineBtnStyle}>View</button>
            {/* Mobile parity with the desktop kebab menu (spec AC9) — C5-30. */}
            {canApprove && e.status === "confirmed" && branchesEnabled && (
              <button onClick={() => startEditLocations(e)} style={outlineBtnStyle}>Edit locations</button>
            )}
            {canApprove && e.status === "confirmed" && (
              <AsyncButton onClick={() => reprocessMut.mutateAsync(e.id)} title="Re-run AI extraction on the original document"
                style={{ ...outlineBtnStyle, color: "#1d4ed8", borderColor: "#93c5fd" }}>
                Retry Extraction
              </AsyncButton>
            )}
          </>
        )}
        {/* Delete is available to anyone who can reach this page, on any still-open or
            failed capture (matches the backend's _EDITABLE_STATUSES check) — not gated
            on canDecide, since an approver deleting a bad scan is just as valid as a
            non-approver doing so. */}
        {DELETABLE_STATUSES.has(e.status) && (
          <AsyncButton onClick={() => handleDelete(e.id)} style={{ ...outlineBtnStyle, color: "#dc2626" }}>Delete</AsyncButton>
        )}
      </>
    );
  }

  // Desktop table row actions split into one inline primary button + a ⋮ kebab menu for
  // the rest (spec expense-capture-ui-redesign AC11) — same visibility rules as
  // renderCaptureActions above (which the mobile card keeps using unchanged, full inline).
  // The page-level LoadingOverlay already covers confirm/reject/reprocess/delete pending
  // state, so menu items call `.mutate` directly rather than needing their own AsyncButton.
  function primaryCaptureAction(e: any): React.ReactNode {
    const processing = e.status === "processing";
    if (processing) return <span style={{ fontSize: 12, color: "#1d4ed8" }}>Extracting…</span>;
    if (e.status === "extraction_failed") {
      return (
        <button onClick={() => startEdit(e)} style={{ ...outlineBtnStyle, color: "#dc2626", borderColor: "#fca5a5" }}>
          Enter manually
        </button>
      );
    }
    const canDecide = canApprove && OPEN_STATUSES.has(e.status);
    if (canDecide) return <button onClick={() => startEdit(e)} style={outlineBtnStyle}>Review</button>;
    if (OPEN_STATUSES.has(e.status)) return <button onClick={() => startEdit(e)} style={outlineBtnStyle}>Edit</button>;
    return <button onClick={() => startView(e)} style={outlineBtnStyle}>View</button>;
  }

  function secondaryCaptureActions(e: any): DropdownMenuItem[] {
    if (e.status === "processing" || e.status === "extraction_failed") {
      // Delete is still available for a failed extraction — same rule as today.
      return e.status === "extraction_failed"
        ? [{ label: "Delete", danger: true, onClick: () => handleDelete(e.id) }]
        : [];
    }
    const canDecide = canApprove && OPEN_STATUSES.has(e.status);
    const isDeliveryNote = e.doc_class === "delivery_note";
    const items: DropdownMenuItem[] = [];
    // Resolving a flagged duplicate is approver-gated server-side (resolve_duplicate calls
    // _require_approver), so offering it to anyone else would only produce a 403.
    if (e.duplicate_status === "possible" && canApprove) {
      items.push({ label: "Keep both (not a duplicate)", onClick: () => resolveDupMut.mutate({ id: e.id, action: "keep_both" }) });
      items.push({ label: "Discard as duplicate", danger: true, onClick: () => resolveDupMut.mutate({ id: e.id, action: "discard" }) });
    }
    if (canDecide) {
      if (!isDeliveryNote) items.push({ label: "Confirm", onClick: () => confirmMut.mutate(e.id) });
      items.push({ label: "Reject", danger: true, onClick: () => rejectMut.mutate(e.id) });
    }
    if (!OPEN_STATUSES.has(e.status) && canApprove && e.status === "confirmed") {
      // C5-30: correcting a confirmed capture's branch allocation. Approver-only, matching
      // the server-side gate — and only worth offering when locations are in use at all.
      if (branchesEnabled) {
        items.push({ label: "Edit locations", onClick: () => startEditLocations(e) });
      }
      items.push({ label: "Retry Extraction", onClick: () => reprocessMut.mutate(e.id) });
    }
    if (DELETABLE_STATUSES.has(e.status)) {
      items.push({ label: "Delete", danger: true, onClick: () => handleDelete(e.id) });
    }
    return items;
  }

  function SortHeader({ column, label, align }: { column: string; label: string; align?: "right" }) {
    const active = sortBy === column;
    return (
      <th onClick={() => toggleSort(column)} style={{
        padding: "10px 12px", textAlign: align || "left", fontSize: 12, fontWeight: 600,
        color: active ? NAVY : "#64748b", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
      }}>
        {label}{active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
      </th>
    );
  }

  // Desktop table row — dense presentation of the same data/actions as the mobile card
  // below (spec AC1/AC9). A capture with warnings (duplicate/failed/paired-notes/bulk-
  // failure) gets a second, full-width sub-row underneath so variable-length content
  // doesn't distort the main row's height.
  function renderCaptureTableRow(e: any) {
    const processing = e.status === "processing";
    const isDuplicate = e.duplicate_status === "possible";
    const hasWarnings = e.status === "extraction_failed" || e.status === "discarded" || isDuplicate ||
      e.paired_delivery_note_ids?.length > 0 || bulkFailures[e.id] || (processing && e.original_filename) ||
      !!captureMathWarning(e);
    const rowBg = processing ? "#eff6ff" : e.status === "needs_review" ? "#fffbeb" : isDuplicate ? "#fef2f2" : "white";

    return (
      <React.Fragment key={e.id}>
        <tr style={{ background: rowBg }}>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
            {SELECTABLE_STATUSES.has(e.status) && (
              <input type="checkbox" checked={selectedIds.has(e.id)} onChange={() => toggleSelected(e.id)}
                aria-label={`Select ${e.vendor_name || "capture"} for bulk action`}
                style={{ width: 16, height: 16, cursor: "pointer", verticalAlign: "middle", marginRight: 8 }} />
            )}
            <span style={{ color: "#94a3b8", fontSize: 12, fontFamily: "monospace" }}>{e.sequence_number ?? "—"}</span>
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0", fontSize: 13 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {processing && <Spinner size={14} />}
              <span style={{ fontWeight: 600, color: processing ? "#1d4ed8" : "#1e293b" }}>
                {processing ? "Processing…" : e.vendor_name}
              </span>
            </div>
            {/* C5-27: vendor_name_en is extracted, stored, editable and exported, but the
                table showed only the primary (often Thai) name — so an English-reading
                reviewer had to open each row to identify the vendor. Shown as a second
                line rather than inline, and only when it differs from the primary name. */}
            {!processing && e.vendor_name_en && e.vendor_name_en !== e.vendor_name && (
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>{e.vendor_name_en}</div>
            )}
            {!processing && (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4, alignItems: "center" }}>
                {renderCaptureBadges(e, { includeStatus: false })}
                {/* Restores the confidence % that used to be visible in the old card
                    list — dropped for desktop when this became a table (still shown on
                    the mobile card below); QA flagged it missing on 2026-07-10. */}
                {e.confidence_score != null && (
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>Confidence: {Math.round(e.confidence_score)}%</span>
                )}
              </div>
            )}
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0", fontSize: 13, color: "#475569" }}>
            {processing ? "—" : e.category}
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0" }}>
            <span style={{
              background: (STATUS_CONFIG[e.status] || STATUS_CONFIG.needs_review).bg,
              color: (STATUS_CONFIG[e.status] || STATUS_CONFIG.needs_review).text,
              border: `1px solid ${(STATUS_CONFIG[e.status] || STATUS_CONFIG.needs_review).border}`,
              padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
            }}>
              {(STATUS_CONFIG[e.status] || STATUS_CONFIG.needs_review).label}
            </span>
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0", fontSize: 13, textAlign: "right", fontWeight: 600, color: NAVY, whiteSpace: "nowrap" }}>
            {e.total > 0 ? `${e.currency || "THB"} ${(e.total || 0).toLocaleString("en", { minimumFractionDigits: 2 })}` : "—"}
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0", fontSize: 13, whiteSpace: "nowrap" }}>
            {e.expense_date || "—"}
          </td>
          <td style={{ padding: "10px 12px", borderTop: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              {primaryCaptureAction(e)}
              {/* Distinct accessible name per row (not the header's generic "More
                  actions") — both a screen-reader disambiguation and what keeps this
                  from colliding with the header overflow menu in role-based selectors. */}
              <DropdownMenu label="⋮" title={`More actions for ${e.vendor_name || "capture"}`} align="right"
                style={{ ...outlineBtnStyle, padding: "4px 9px", fontWeight: 700 }}
                items={secondaryCaptureActions(e)} />
            </div>
          </td>
        </tr>
        {hasWarnings && (
          <tr style={{ background: rowBg }}>
            <td colSpan={7} style={{ padding: "0 12px 10px" }}>{renderCaptureWarnings(e)}</td>
          </tr>
        )}
      </React.Fragment>
    );
  }

  // Mobile stacked layout (below the isMobile breakpoint) — same data/state/actions as
  // the desktop table, just a different presentation (spec AC9).
  function renderCaptureCard(e: any) {
    const processing = e.status === "processing";
    const isDuplicate = e.duplicate_status === "possible";

    return (
      <div key={e.id} style={{
        background: processing ? "#eff6ff" : e.status === "needs_review" ? "#fffbeb" : isDuplicate ? "#fef2f2" : "white",
        border: `1px solid ${processing ? "#93c5fd" : e.status === "needs_review" ? "#fde68a" : isDuplicate ? "#fecaca" : "#e2e8f0"}`,
        borderRadius: 12, padding: "16px 20px",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        flexWrap: "wrap", gap: 10,
      }}>
        {SELECTABLE_STATUSES.has(e.status) && (
          <input type="checkbox" checked={selectedIds.has(e.id)} onChange={() => toggleSelected(e.id)}
            aria-label={`Select ${e.vendor_name || "capture"} for bulk action`}
            style={{ width: 18, height: 18, flexShrink: 0, cursor: "pointer" }} />
        )}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ color: "#94a3b8", fontSize: 12, fontFamily: "monospace", flexShrink: 0 }} title="Document number (day+month uploaded, serial resets each month)">
              {e.sequence_number ?? "—"}
            </span>
            {processing && <Spinner />}
            <span style={{ fontWeight: 600, fontSize: 14, color: processing ? "#1d4ed8" : "#1e293b" }}>
              {processing ? "Processing…" : e.vendor_name}
            </span>
            {!processing && (
              <span style={{ background: "#f1f5f9", color: "#475569", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>
                {e.category}
              </span>
            )}
            {renderCaptureBadges(e)}
          </div>

          {!processing && (
            <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#64748b", flexWrap: "wrap" }}>
              {e.total > 0 && (
                <span style={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>
                  {e.currency || "THB"} {(e.total || 0).toLocaleString("en", { minimumFractionDigits: 2 })}
                </span>
              )}
              {e.expense_date && <span>Date: {e.expense_date}</span>}
              {e.vat_amount > 0 && <span>VAT: {(e.vat_amount || 0).toFixed(2)}</span>}
              {e.confidence_score != null && <span>Confidence: {Math.round(e.confidence_score)}%</span>}
            </div>
          )}

          {renderCaptureWarnings(e, { inlineDuplicateActions: true })}
        </div>

        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {renderCaptureActions(e)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <LoadingOverlay active={
        createMut.isPending || updateMut.isPending || deleteMut.isPending ||
        confirmMut.isPending || rejectMut.isPending || reprocessMut.isPending || decisionPending ||
        bulkPending || resolveDupMut.isPending || exportingCsv || exportingPdf
      } />
      {/* Hidden camera input for mobile single-shot capture */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        {...(isMobile ? { capture: "environment" as const } : {})}
        style={{ display: "none" }}
        onChange={async e => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) await handleAsyncUpload([file], "camera");
        }}
      />

      {/* Own title/subtitle removed (spec expense-capture-ui-redesign AC5) — the app shell's
          top bar already renders "AI Expense Capture" + subtitle for this route from
          PAGE_NAMES/PAGE_SUBTITLES in App.tsx, so this was a duplicate. Only the actions
          row remains here.
          Upload Receipt(s) is deliberately NOT tab-gated — per design it's a quick-capture
          action available from every tab (Ledger/Branches/Insights/Settings too), not just
          the Capture list. Everything else in this header (Confirm-all, Export, ⋯) only
          makes sense in the context of the capture list, so those stay Capture-tab-only. */}
      <div className="page-header">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {/* Async multi-file upload — the file input IS the control, overlaid at opacity 0 so
              the user's tap hits it directly; a programmatic .click() on a hidden input does not
              work in Chrome DevTools simulation or on some real mobile browsers.
              The input comes first in DOM order and sits on top via z-index, which leaves the
              <button> purely decorative: aria-hidden + tabIndex -1 take it out of the
              accessibility tree and the tab order. Without that, keyboard users tab onto a
              <button> that does nothing while the real control has no accessible name.
              `.upload-receipts` paints the focus ring onto the button while the invisible input
              holds focus. */}
          <div className="upload-receipts" style={{ position: "relative", display: "inline-flex" }}>
            <input
              type="file"
              aria-label="Upload receipts"
              accept="image/jpeg,image/png,.pdf"
              multiple
              disabled={!!uploadProgress}
              style={{
                position: "absolute", inset: 0, opacity: 0, zIndex: 1,
                cursor: uploadProgress ? "not-allowed" : "pointer",
                width: "100%", height: "100%",
              }}
              onChange={e => { const files = Array.from(e.target.files ?? []); e.target.value = ""; if (files.length) handleAsyncUpload(files); }}
            />
            <button
              aria-hidden="true"
              tabIndex={-1}
              disabled={!!uploadProgress}
              style={{
                ...primaryBtnStyle, display: "inline-flex", alignItems: "center", gap: 6,
                cursor: uploadProgress ? "not-allowed" : "pointer",
                opacity: uploadProgress ? 0.6 : 1, minHeight: 40,
              }}
            >
              {uploadProgress
                ? `Uploading ${uploadProgress.total} file${uploadProgress.total !== 1 ? "s" : ""}…`
                : "Upload Receipt(s)"}
            </button>
          </div>

          {tab === "capture" && (
          <>
          {/* Contextual — only rendered at all when there's something to confirm (AC4).
              On mobile this becomes a banner instead (AC8), so the button is desktop-only. */}
          {!isMobile && canApprove && readyToConfirmCount > 0 && (
            <button onClick={() => setShowBulkConfirm(true)} style={{ ...outlineBtnStyle, color: "#16a34a", borderColor: "#86efac", minHeight: 40, fontWeight: 600 }}>
              Confirm all high-confidence ({readyToConfirmCount})
            </button>
          )}

          {/* Desktop: Export is its own dropdown (AC2). Mobile flattens Export CSV/PDF
              into the single ⋯ menu below instead (AC7) — no nested menus on touch. */}
          {!isMobile && (
            <DropdownMenu label="Export ▾" style={{ ...outlineBtnStyle, minHeight: 40 }}
              items={[
                { label: exportingCsv ? "Exporting CSV…" : "Export CSV", disabled: exportingCsv, onClick: handleExportCsv },
                { label: exportingPdf ? "Exporting PDF…" : "Export PDF", disabled: exportingPdf, onClick: handleExportPdf },
              ]} />
          )}

          <DropdownMenu label="⋯" title="More actions" style={{ ...outlineBtnStyle, minHeight: 40, padding: "6px 12px", fontWeight: 700 }}
            items={[
              ...(isMobile ? [{ label: "Camera", onClick: () => cameraInputRef.current?.click() }] : []),
              {
                label: "+ Manual Entry",
                onClick: () => { setEditingId(null); setViewOnly(false); setForm(BLANK_FORM); setShowForm(true); },
              },
              ...(isMobile ? [
                { label: exportingCsv ? "Exporting CSV…" : "Export CSV", disabled: exportingCsv, onClick: handleExportCsv },
                { label: exportingPdf ? "Exporting PDF…" : "Export PDF", disabled: exportingPdf, onClick: handleExportPdf },
              ] : []),
            ]} />
          </>
          )}
        </div>

        {/* Mobile-only contextual confirm banner, replacing the desktop button above (AC8). */}
        {tab === "capture" && isMobile && canApprove && readyToConfirmCount > 0 && (
          <button onClick={() => setShowBulkConfirm(true)} style={{
            display: "block", width: "100%", textAlign: "left", marginTop: 10,
            background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8,
            padding: "10px 14px", fontSize: 13, color: "#166534", cursor: "pointer",
          }}>
            {readyToConfirmCount} item{readyToConfirmCount !== 1 ? "s" : ""} ready to confirm — <strong>Confirm all</strong>
          </button>
        )}
      </div>

      {/* Tab pill bar removed — the module sidebar is the switcher now
          (Captures / Ledger / Branches / Insights / Settings). */}

      {tab === "capture" && (
      <>
      {uploadProgress && (
        <div style={{
          background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8,
          padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10, fontSize: 14,
        }}>
          <Spinner /> Uploading {uploadProgress.total} file{uploadProgress.total !== 1 ? "s" : ""} — please wait…
        </div>
      )}

      {hasPending && !uploadProgress && (
        <div style={{
          background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 8,
          padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#1d4ed8",
        }}>
          <Spinner /> Processing in the background — this page updates automatically…
        </div>
      )}

      <div className="filter-bar" style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {/* extraction_failed was missing here: a capture whose OCR died was reachable only
            by knowing to type the status into the URL. The module dashboard's "Extraction
            Failed" card links straight to it, so it needs a chip to land on.
            discarded was missing for the same reason (C5-17). The pipeline assigns it to
            any document it classifies as not_an_expense, and it is terminal — so those
            captures existed, counted against the tenant's processed-document quota, and
            could not be filtered to or explained. They only ever appeared buried in "All",
            which is precisely why an uploaded file looked like it had vanished. */}
        {["", "needs_review", "ready_to_confirm", "extraction_failed", "confirmed", "rejected", "discarded"].map(s => (
          <button key={s || "all"} onClick={() => { setStatusFilter(s); setSelectedIds(new Set()); setBulkFailures({}); }} style={{
            padding: "6px 14px", borderRadius: 20, border: "1px solid #e2e8f0", cursor: "pointer",
            fontSize: 13, fontWeight: 500,
            background: statusFilter === s ? NAVY : "white",
            color: statusFilter === s ? "white" : "#64748b",
          }}>{s ? STATUS_CONFIG[s]?.label : "All"}</button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: showFilters || !isMobile ? 8 : 16, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label style={labelStyle}>Search</label>
          {/* C5-24: "doc #" used to mean only the vendor's own number off the printed
              document — never the DDMM-Serial in the "#" column, which is the one the
              user can actually see. Both match now, and the title spells out which is
              which so the placeholder doesn't have to. */}
          <input type="text" placeholder="Doc #, vendor, notes, date…" value={searchInput}
            title={"Matches the document number in the # column (e.g. 1007-0003), the vendor's "
              + "own document number, vendor name, notes, line items, and the expense date."}
            onChange={e => setSearchInput(e.target.value)}
            style={{ ...inputStyle, width: 220 }} />
        </div>
        {/* Category/From/To/Location/Sort collapse behind this on mobile (AC10) — search
            stays visible either way so narrowing results doesn't need an extra tap. */}
        {isMobile && (
          <button onClick={() => setShowFilters(s => !s)} style={{ ...outlineBtnStyle, minHeight: 34, fontWeight: activeFilterCount > 0 ? 700 : 400 }}>
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
        )}
      </div>

      {(!isMobile || showFilters) && (
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label style={labelStyle}>Category</label>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ ...inputStyle, width: 160 }}>
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>From</label>
          <DateInput value={dateFromFilter} onChange={e => setDateFromFilter(e.target.value)} style={{ ...inputStyle, width: 140 }} />
        </div>
        <div>
          <label style={labelStyle}>To</label>
          <DateInput value={dateToFilter} onChange={e => setDateToFilter(e.target.value)} style={{ ...inputStyle, width: 140 }} />
        </div>
        {branchesEnabled && (
          <div>
            <label style={labelStyle}>Location</label>
            <select value={branchFilterId} onChange={e => setBranchFilterId(e.target.value ? Number(e.target.value) : "")} style={{ ...inputStyle, width: 160 }}>
              <option value="">All locations</option>
              {activeBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label style={labelStyle}>Sort by</label>
          {/* Always visible on both layouts — the desktop table's clickable column
              headers set the same sortBy/sortDir state, but mobile has no header row to
              click, so this is the only sort control there (spec AC9: same feature set). */}
          <div style={{ display: "flex", gap: 4 }}>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...inputStyle, width: 110 }}>
              <option value="">Default</option>
              <option value="date">Date</option>
              <option value="total">Total</option>
              <option value="vendor">Vendor</option>
              <option value="status">Status</option>
            </select>
            <button onClick={() => setSortDir(d => (d === "asc" ? "desc" : "asc"))}
              title={sortDir === "asc" ? "Ascending" : "Descending"}
              style={{ ...outlineBtnStyle, minHeight: 34, padding: "6px 10px" }}>
              {sortDir === "asc" ? "▲" : "▼"}
            </button>
          </div>
        </div>
        {(searchInput || categoryFilter || dateFromFilter || dateToFilter || branchFilterId !== "") && (
          <button onClick={() => { setSearchInput(""); setCategoryFilter(""); setDateFromFilter(""); setDateToFilter(""); setBranchFilterId(""); }}
            style={{ ...outlineBtnStyle, minHeight: 34 }}>
            Clear filters
          </button>
        )}
      </div>
      )}

      {/* Manual entry / edit / review — a modal (not inline) so opening it from a card
          scrolled below the fold always brings it into view, regardless of list length or
          scroll position (QA bug #3: opening Review from a low card rendered the form off-
          screen above the current scroll position, with no indication it had opened). Same
          overlay pattern as ExpenseBulkConfirmSheet: fixed backdrop, centered scrollable box. */}
      {showForm && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={closeForm}
        >
        <div onClick={e => e.stopPropagation()}
          style={{ background: "white", borderRadius: 12, padding: 24, width: 900, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ margin: 0, color: NAVY, fontSize: 16 }}>
              {editingId != null
                ? (splitsOnly ? "Edit Locations" : viewOnly ? "View Expense Capture" : "Review Expense Capture")
                : "New Expense Capture"}
              {editingStatus === "extraction_failed" && editingOriginalFilename && (
                <span style={{ marginLeft: 10, fontWeight: 400, fontSize: 12, color: "#64748b" }}>({editingOriginalFilename})</span>
              )}
            </h3>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!viewOnly && (
              <label style={{ ...outlineBtnStyle, minHeight: 44, display: "inline-flex", alignItems: "center", cursor: scanning ? "not-allowed" : "pointer", opacity: scanning ? 0.6 : 1 }}>
                {scanning ? "Scanning…" : editingId != null ? "Re-scan Document" : "Scan Document"}
                <input type="file" accept="image/jpeg,image/png,.pdf" disabled={scanning} style={{ display: "none" }}
                  onChange={async e => {
                    const file = e.target.files?.[0]; e.target.value = "";
                    if (file) await handleScanReceipt(file);
                  }} />
              </label>
            )}
            <button type="button" onClick={closeForm} aria-label="Close"
              style={{ background: "none", border: "none", fontSize: 20, lineHeight: 1, color: "#94a3b8", cursor: "pointer", padding: 4 }}>
              ✕
            </button>
            </div>
          </div>

          {aiFilledFields.size > 0 && (
            <div style={{ background: "#fefce8", border: "1px solid #fde047", borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 12, color: "#854d0e" }}>
              Fields highlighted in yellow were filled by AI — please verify before saving.
              {lowConfidenceFields.size > 0 && (
                <span style={{ color: "#c2410c" }}> Orange fields are below {HIGH_CONFIDENCE}% confidence — double-check against the document.</span>
              )}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "40% 1fr", gap: 20, marginBottom: 14 }}>
            <div>
              {!viewOnly && (
                <div style={{ marginBottom: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <label style={{ ...outlineBtnStyle, minHeight: 44, display: "inline-flex", alignItems: "center", cursor: "pointer" }}>
                    Choose File
                    <input type="file" accept="image/*,.pdf" style={{ display: "none" }}
                      onChange={e => { const f = e.target.files?.[0]; if (f) selectReceiptFile(f); }} />
                  </label>
                  {isMobile && (
                    <button type="button" onClick={() => cameraInputRef.current?.click()} style={{ ...outlineBtnStyle, minHeight: 44 }}>
                      Camera
                    </button>
                  )}
                  {receiptFile && <div style={{ fontSize: 12, color: "#64748b" }}>{receiptFile.name}</div>}
                </div>
              )}
              <ReceiptPanel previewUrl={receiptPreview} mimeType={receiptMimeType} isMobile={isMobile} onFile={viewOnly ? undefined : selectReceiptFile} isLoading={receiptLoading} />
            </div>

            <div>
              <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={labelStyle}>Vendor Name *</label>
                  <input disabled={viewOnly} value={form.vendor_name} onChange={e => { clearAiMark("vendor_name"); setForm(f => ({ ...f, vendor_name: e.target.value })); }} style={aiStyle("vendor_name")} />
                  {fieldErrors.vendor_name && <div style={errorTextStyle}>{fieldErrors.vendor_name}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Document Number</label>
                  <input disabled={viewOnly} value={form.doc_number} onChange={e => { clearAiMark("doc_number"); setForm(f => ({ ...f, doc_number: e.target.value })); }} style={aiStyle("doc_number")} />
                </div>
                <div>
                  <label style={labelStyle}>Date *</label>
                  <DateInput disabled={viewOnly} max={todayLocalISO()} value={form.expense_date} onChange={e => { clearAiMark("expense_date"); setForm(f => ({ ...f, expense_date: e.target.value })); }} style={aiStyle("expense_date")} />
                  {fieldErrors.expense_date && <div style={errorTextStyle}>{fieldErrors.expense_date}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Category *</label>
                  <select disabled={viewOnly} value={form.category} onChange={e => { clearAiMark("category"); setForm(f => ({ ...f, category: e.target.value })); }} style={aiStyle("category")}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {fieldErrors.category && <div style={errorTextStyle}>{fieldErrors.category}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Subtotal *</label>
                  <input disabled={viewOnly} type="number" min={0} step="0.01" value={form.subtotal} onKeyDown={blockNonNumericKeys} onChange={e => { clearAiMark("subtotal"); setForm(f => ({ ...f, subtotal: e.target.value })); }} style={aiStyle("subtotal")} />
                  {fieldErrors.subtotal && <div style={errorTextStyle}>{fieldErrors.subtotal}</div>}
                </div>
                <div>
                  <label style={labelStyle}>VAT Amount</label>
                  <input disabled={viewOnly} type="number" min={0} step="0.01" value={form.vat_amount} onKeyDown={blockNonNumericKeys} onChange={e => { clearAiMark("vat_amount"); setForm(f => ({ ...f, vat_amount: e.target.value })); }} style={aiStyle("vat_amount")} />
                </div>
                <div>
                  <label style={labelStyle}>Total</label>
                  <input disabled={viewOnly} type="number" min={0} step="0.01" value={form.total}
                    onKeyDown={blockNonNumericKeys}
                    onChange={e => { clearAiMark("total"); setForm(f => ({ ...f, total: e.target.value })); }}
                    style={aiStyle("total")} />
                  {/* Bug #87: total no longer silently forces itself to subtotal+VAT — this
                      is a cross-check hint only, shown whenever the two diverge, so a real
                      service charge/discount/rounding (or a genuine AI misread) is visible
                      without blocking the save.

                      C5-42 asked for withholding tax to be folded into this check. It is not
                      a calculation error: a Thai tax invoice's total IS subtotal + VAT, and
                      WHT is deducted by the payer at payment — it never reduces the document
                      total. The arithmetic was right; the wording was wrong. It asserted an
                      accounting rule, when all this module can honestly claim is that the
                      three numbers it read do not agree. Reworded as a read-quality prompt,
                      naming WHT among the ordinary explanations so a reviewer stops treating
                      it as a fault to fix (PO ruling 2026-07-31: capture-only). */}
                  {form.total !== "" && Math.abs(enteredTotal() - calcTotal()) > 0.01 && (
                    <div style={{ fontSize: 11, color: "#c2410c", marginTop: 4 }}>
                      These figures don't add up: Subtotal + VAT = {calcTotal().toFixed(2)}, but Total reads
                      {" "}{enteredTotal().toFixed(2)}. Worth checking against the document — a service charge,
                      discount, rounding, or a total shown net of withholding tax all explain it. Withholding
                      tax is not deducted here; it's recorded separately for your accountant.
                    </div>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Currency</label>
                  <select disabled={viewOnly} value={form.currency} onChange={e => { clearAiMark("currency"); setForm(f => ({ ...f, currency: e.target.value })); }} style={aiStyle("currency")}>
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Document Class</label>
                  <select disabled={viewOnly} value={form.doc_class} onChange={e => { clearAiMark("doc_class"); setForm(f => ({ ...f, doc_class: e.target.value })); }} style={aiStyle("doc_class")}>
                    <option value="">—</option>
                    {Object.entries(DOC_CLASSES).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Tax ID (เลขประจำตัวผู้เสียภาษี)</label>
                  <input disabled={viewOnly} maxLength={13} value={form.vendor_tax_id} placeholder="13 digits"
                    onChange={e => { clearAiMark("vendor_tax_id"); setForm(f => ({ ...f, vendor_tax_id: e.target.value.replace(/\D/g, "") })); }}
                    style={aiStyle("vendor_tax_id")} />
                </div>
                <div>
                  <label style={labelStyle}>Branch (สาขาที่)</label>
                  <input disabled={viewOnly} value={form.tax_branch_no} placeholder="e.g. 00001 / สำนักงานใหญ่"
                    onChange={e => { clearAiMark("tax_branch_no"); setForm(f => ({ ...f, tax_branch_no: e.target.value })); }}
                    style={aiStyle("tax_branch_no")} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Vendor Name (English)</label>
                  <input disabled={viewOnly} value={form.vendor_name_en}
                    onChange={e => { clearAiMark("vendor_name_en"); setForm(f => ({ ...f, vendor_name_en: e.target.value })); }}
                    style={aiStyle("vendor_name_en")} />
                </div>
                <div>
                  <label style={labelStyle}>
                    WHT / Withholding Tax
                    {form.wht_applicable === null && <span style={{ color: "#92400e", fontWeight: 700 }}> — unanswered</span>}
                  </label>
                  <select disabled={viewOnly}
                    value={form.wht_applicable === null ? "" : String(form.wht_applicable)}
                    onChange={e => {
                      const v = e.target.value;
                      // Switching away from "Yes — service" hides the rate field, so drop
                      // any outstanding error on it rather than leaving it in state to
                      // reappear if the user switches back.
                      clearFieldError("wht_rate");
                      setForm(f => ({ ...f, wht_applicable: v === "" ? null : v === "true" }));
                    }}
                    style={inputStyle}>
                    <option value="">— Not reviewed —</option>
                    <option value="false">No — goods (WHT doesn't apply)</option>
                    <option value="true">Yes — service (WHT applies)</option>
                  </select>
                </div>
                {form.wht_applicable === true && (
                  <div>
                    <label style={labelStyle}>WHT Rate (%)</label>
                    <input disabled={viewOnly} type="number" min={0} max={100} step="0.01"
                      placeholder="e.g. 3" value={form.wht_rate}
                      onKeyDown={blockInvalidNumberKey}
                      onChange={e => { clearFieldError("wht_rate"); setForm(f => ({ ...f, wht_rate: e.target.value })); }}
                      style={inputStyle} />
                    {fieldErrors.wht_rate && <div style={errorTextStyle}>{fieldErrors.wht_rate}</div>}
                  </div>
                )}
              </div>

              {branchesEnabled && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <label style={labelStyle}>Location</label>
                    {/* Not offered in splits-only mode: unticking it would look like it
                        clears the allocation, but clearing splits goes through the record
                        update endpoint (clear_splits), which this mode deliberately never
                        calls — so the control would silently do nothing. */}
                    {canEditSplits && !splitsOnly && editingId != null && (
                      <label style={{ fontSize: 12, color: "#64748b", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input type="checkbox" checked={splitMode} onChange={e => {
                          setSplitMode(e.target.checked);
                          if (e.target.checked && splitRows.length === 0) setSplitRows([{ branch_id: "", amount: form.subtotal }]);
                        }} />
                        Split across locations
                      </label>
                    )}
                  </div>
                  {!splitMode ? (
                    <select disabled={viewOnly} value={form.branch_id ?? ""}
                      onChange={e => setForm(f => ({ ...f, branch_id: e.target.value ? Number(e.target.value) : null }))}
                      style={inputStyle}>
                      <option value="">— (head office / unassigned)</option>
                      {activeBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  ) : (
                    <div>
                      {splitRows.map((row, i) => (
                        <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 8, marginBottom: 6 }}>
                          <select disabled={!canEditSplits} value={row.branch_id}
                            onChange={e => setSplitRows(rs => rs.map((r, idx) => idx === i ? { ...r, branch_id: e.target.value ? Number(e.target.value) : "" } : r))}
                            style={inputStyle}>
                            <option value="">Select location…</option>
                            {activeBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                          </select>
                          <input disabled={!canEditSplits} type="number" min={0} step="0.01" placeholder="Amount" value={row.amount}
                            onKeyDown={blockNonNumericKeys}
                            onChange={e => setSplitRows(rs => rs.map((r, idx) => idx === i ? { ...r, amount: e.target.value } : r))}
                            style={inputStyle} />
                          {canEditSplits && splitRows.length > 1 && (
                            <button type="button" onClick={() => setSplitRows(rs => rs.filter((_, idx) => idx !== i))}
                              style={{ ...outlineBtnStyle, color: "#dc2626", padding: "4px 10px" }}>✕</button>
                          )}
                        </div>
                      ))}
                      {canEditSplits && (
                        <button type="button" onClick={() => setSplitRows(rs => [...rs, { branch_id: "", amount: "" }])} style={outlineBtnStyle}>
                          + Add location
                        </button>
                      )}
                      {/* Live running total (C5-30) — QA's "Branches — total": the sum of
                          what's been allocated so far, against what it has to reach. */}
                      {(() => {
                        const allocated = splitAllocated();
                        const target = enteredTotal();
                        const matches = Math.abs(allocated - target) <= 0.01;
                        return (
                          <div style={{ fontSize: 12, marginTop: 6, color: matches ? "#16a34a" : "#c2410c", fontWeight: 600 }}>
                            Locations — total: {allocated.toFixed(2)} of {target.toFixed(2)}
                            {matches ? " ✓" : ` (${(target - allocated) >= 0 ? "" : "+"}${(target - allocated).toFixed(2)} to allocate)`}
                          </div>
                        );
                      })()}
                      {editingStatus !== "confirmed" && (
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                          The Branches tab shows confirmed spend only — this allocation appears there once the expense is confirmed.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Notes</label>
                <textarea disabled={viewOnly} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2} style={{ ...inputStyle, resize: "vertical" }} />
              </div>

              {/* Splits-only mode saves the allocation alone — the record is untouched, so
                  there's nothing here that could re-trip the duplicate guard (C5-30). */}
              {splitsOnly && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={handleSaveSplitsOnly} disabled={decisionPending}
                    style={{ ...primaryBtnStyle, minHeight: 44 }}>
                    {decisionPending ? "Saving…" : "Save Locations"}
                  </button>
                  <button onClick={closeForm} disabled={decisionPending} style={{ ...outlineBtnStyle, minHeight: 44 }}>
                    Cancel
                  </button>
                </div>
              )}

              {!viewOnly && (() => {
                const canDecide = editingId != null && OPEN_STATUSES.has(editingStatus || "") && canApprove;
                // C5-45: Save and Confirm are no longer disabled on incomplete input.
                // A disabled button is a dead end — it withholds both the action and the
                // reason. They stay live and validateForm() names what's missing.
                // `disabled` now means only "a request is already in flight".
                const busy = createMut.isPending || updateMut.isPending || decisionPending;
                return (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button onClick={handleSubmit}
                      disabled={busy}
                      style={{ ...(canDecide ? outlineBtnStyle : primaryBtnStyle), minHeight: 44 }}>
                      {(createMut.isPending || updateMut.isPending) ? "Saving…" : (editingId != null ? "Save Changes" : "Save")}
                    </button>
                    {canDecide && (
                      <>
                        {form.doc_class === "delivery_note" ? (
                          <span style={{ fontSize: 12, color: "#854d0e", alignSelf: "center" }}>
                            Delivery note — not an expense, cannot be confirmed
                          </span>
                        ) : form.doc_class === "not_an_expense" ? (
                          <span style={{ fontSize: 12, color: "#854d0e", alignSelf: "center" }}>
                            Not classified as an expense — cannot be confirmed
                          </span>
                        ) : (
                          <button onClick={handleConfirmInReview}
                            disabled={busy}
                            style={{ ...primaryBtnStyle, background: "#16a34a", minHeight: 44 }}>
                            {decisionPending ? "Confirming…" : "Confirm"}
                          </button>
                        )}
                        <button onClick={handleRejectInReview}
                          disabled={decisionPending}
                          style={{ ...outlineBtnStyle, color: "#dc2626", borderColor: "#fca5a5", minHeight: 44 }}>
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div style={{
          background: NAVY, color: "white", borderRadius: 8, padding: "10px 16px", marginBottom: 12,
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 13,
        }}>
          <strong>{selectedIds.size} selected</strong>
          {canApprove && (
            <AsyncButton onClick={handleBulkConfirm} style={{ ...outlineBtnStyle, background: "white", color: "#16a34a", fontWeight: 600 }}>
              Confirm Selected
            </AsyncButton>
          )}
          {canApprove && (
            <AsyncButton onClick={handleBulkReject} style={{ ...outlineBtnStyle, background: "white", color: "#dc2626" }}>
              Reject Selected
            </AsyncButton>
          )}
          <AsyncButton onClick={handleBulkDelete} style={{ ...outlineBtnStyle, background: "white", color: "#dc2626" }}>
            Delete Selected
          </AsyncButton>
          <button onClick={() => { setSelectedIds(new Set()); setBulkFailures({}); }}
            style={{ ...outlineBtnStyle, background: "transparent", color: "white", borderColor: "rgba(255,255,255,0.4)", marginLeft: "auto" }}>
            Clear
          </button>
        </div>
      )}

      {isLoading && <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>Loading expense captures…</div>}
      {isError  && <div style={{ textAlign: "center", padding: "40px 0", color: "#dc2626" }}>Something went wrong loading expense captures.</div>}

      {!isLoading && !isError && captures.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          {(search || categoryFilter || dateFromFilter || dateToFilter || branchFilterId !== "" || statusFilter) ? (
            <>No expense captures match these filters.</>
          ) : (
            <>No expense captures yet.<br /><span style={{ fontSize: 13 }}>Use "Upload Receipt(s)" to process documents in the background.</span></>
          )}
        </div>
      )}

      {!isLoading && !isError && captures.length > 0 && (
        isMobile ? (
          <div style={{ display: "grid", gap: 10 }}>
            {captures.map(renderCaptureCard)}
          </div>
        ) : (
          <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#64748b" }}>#</th>
                  <SortHeader column="vendor" label="Vendor" />
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#64748b" }}>Category</th>
                  <SortHeader column="status" label="Status" />
                  <SortHeader column="total" label="Total" align="right" />
                  <SortHeader column="date" label="Date" />
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#64748b" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {captures.map(renderCaptureTableRow)}
              </tbody>
            </table>
          </div>
        )
      )}

      {!isLoading && !isError && totalCount > 0 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#64748b" }}>
            {totalCount} capture{totalCount !== 1 ? "s" : ""} — page {currentPage} of {totalPages}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}
              style={{ ...outlineBtnStyle, opacity: currentPage <= 1 ? 0.5 : 1, cursor: currentPage <= 1 ? "not-allowed" : "pointer" }}>
              Previous
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
              style={{ ...outlineBtnStyle, opacity: currentPage >= totalPages ? 0.5 : 1, cursor: currentPage >= totalPages ? "not-allowed" : "pointer" }}>
              Next
            </button>
          </div>
        </div>
      )}
      {/* Clears the floating chat avatar (fixed, bottom:24/right:24 — see ChatWidget.tsx),
          which otherwise sits on top of the last row's action buttons at the end of a short
          list (spec expense-capture-ui-redesign AC13). */}
      <div style={{ height: 96 }} aria-hidden="true" />
      </>
      )}

      {tab === "ledger" && <ExpenseLedgerTab branchesEnabled={branchesEnabled} />}

      {tab === "branches" && (
        branchesEnabled ? (
          <ExpenseBranchComparison />
        ) : (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            Branch tracking isn't enabled yet.<br />
            {/* Named for the sidebar item it points at — the "Settings tab" it referred to
                stopped existing when module-subpage-navs made these routes. */}
            <span style={{ fontSize: 13 }}>
              Turn it on in{" "}
              <Link to="/ai-expense-capture/settings" style={{ color: "#1d4ed8", fontWeight: 600 }}>
                AI Expense Settings
              </Link>{" "}
              to tag expenses by location.
            </span>
          </div>
        )
      )}

      {tab === "insights" && <ExpenseInsightsTab />}

      {tab === "settings" && <ExpenseBranchesSettings />}

      {showBulkConfirm && (
        <ExpenseBulkConfirmSheet
          onClose={() => setShowBulkConfirm(false)}
          onDone={() => setShowBulkConfirm(false)}
        />
      )}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: PRIMARY, color: "white", border: "none",
  padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14,
};
const outlineBtnStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", color: "#64748b",
  padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
const labelStyle: React.CSSProperties = {
  display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "#475569",
};
// Same values as ExpensesPage/LeaveManagementPage, so a required-field message looks
// identical wherever the user meets one.
const errorTextStyle: React.CSSProperties = { color: "#dc2626", fontSize: 12, marginTop: 4 };
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
  fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box",
};
